import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { NzModalService } from 'ng-zorro-antd/modal';
import { catchError, finalize, of, tap } from 'rxjs';

import {
  CampaignType,
  IActiveCampaign,
  ICampaign,
  ICampaignDetail,
  ICampaignHistoryItem,
  ICampaignSummary,
  PromotionsService
} from 'core';
import { KorviuLoaderComponent, KorviuNoDataComponent } from 'shared';
import { SwipeNavigationDirective } from 'shared/directives/swipe-slide.directive';

import {
  IPromotionMissionDetailsModalData,
  PromotionMissionDetailsModalComponent
} from '../promotion-mission-details-modal/promotion-mission-details-modal.component';

type MissionStatus = 'active' | 'completed';

interface IMissionStatusFilter {
  id: MissionStatus;
  label: string;
}

interface ICampaignMissionView extends IActiveCampaign {
  progress: number;
  icon: string;
  iconVariant: 'tree' | 'sprout' | 'card';
}

@Component({
  selector: 'app-promotions-missions',
  imports: [KorviuLoaderComponent, KorviuNoDataComponent, RouterLink, TranslatePipe, SwipeNavigationDirective],
  templateUrl: './promotions-missions.component.html',
  styleUrl: './promotions-missions.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PromotionsMissionsComponent implements OnInit {
  readonly #destroyRef = inject(DestroyRef);
  readonly #modalService = inject(NzModalService);
  readonly #promotionsService = inject(PromotionsService);
  readonly #route = inject(ActivatedRoute);

  readonly completedPageSize = 6;

  readonly statusFilters: IMissionStatusFilter[] = [
    { id: 'active', label: 'promotions.missions.status.active' },
    { id: 'completed', label: 'promotions.missions.status.completed' }
  ];

  readonly selectedStatus = signal<MissionStatus>(
    this.#route.snapshot.queryParamMap.get('status') === 'completed' ? 'completed' : 'active'
  );
  readonly selectedFeaturedMissionId = signal<number | null>(null);
  readonly isBannerVisible = signal<boolean>(true);
  readonly completedPage = signal<number>(1);
  readonly activeCampaigns = signal<IActiveCampaign[]>([]);
  readonly activeCampaignsCount = signal<number>(0);
  readonly activeCampaignsIsLoading = signal<boolean>(false);
  readonly activeCampaignsLoadError = signal<boolean>(false);
  readonly completedCampaigns = signal<ICampaignHistoryItem[]>([]);
  readonly completedCampaignsPageMeta = signal({ count: 0, totalPages: 1 });
  readonly completedCampaignsIsLoading = signal<boolean>(false);
  readonly completedCampaignsLoadError = signal<boolean>(false);
  readonly completedCampaignsLoaded = signal<boolean>(false);
  readonly campaignSummary = signal<ICampaignSummary | null>(null);
  readonly detailsLoadingCampaignId = signal<number | null>(null);
  readonly detailsLoadError = signal<boolean>(false);
  readonly campaignMissions = computed<ICampaignMissionView[]>(() =>
    this.activeCampaigns().map(campaign => {
      const objectiveProgress = campaign.objective_progress;

      return {
        ...campaign,
        progress: objectiveProgress ? this.#getProgress(objectiveProgress.current, objectiveProgress.target) : 0,
        icon: campaign.image ?? this.#getCampaignIcon(campaign.campaign_type),
        iconVariant: this.#getCampaignIconVariant(campaign.campaign_type)
      };
    })
  );
  readonly featuredMissions = computed(() => this.campaignMissions().slice(0, 3));
  readonly missions = computed(() => this.campaignMissions().slice(3));
  readonly totalEntriesEarned = computed(() => this.campaignSummary()?.total_entries ?? 0);
  readonly completedCampaignsCount = computed(
    () => this.campaignSummary()?.campaigns_completed ?? this.completedCampaignsPageMeta().count
  );
  readonly totalMissionsCount = computed(() =>
    this.selectedStatus() === 'active' ? this.activeCampaignsCount() : this.completedCampaignsPageMeta().count
  );
  readonly isActiveStatus = computed(() => this.selectedStatus() === 'active');
  readonly completedPages = computed(() => this.completedCampaignsPageMeta().totalPages);
  readonly completedPageNumbers = computed(() =>
    Array.from({ length: this.completedPages() }, (_, index) => index + 1)
  );
  readonly pagedCompletedMissions = computed(() => this.completedCampaigns());
  readonly canGoToPreviousCompletedPage = computed(() => this.completedPage() > 1);
  readonly canGoToNextCompletedPage = computed(() => this.completedPage() < this.completedPages());

  public ngOnInit(): void {
    this.loadActiveCampaigns();
    this.loadCampaignSummary();

    if (this.selectedStatus() === 'completed') {
      this.loadCompletedCampaigns();
    }
  }

  public selectFeaturedMission(missionId: number): void {
    this.selectedFeaturedMissionId.set(missionId);
  }

  public changeFeaturedMission(direction: 1 | -1): void {
    const missions = this.featuredMissions();
    const currentIndex = missions.findIndex(mission => mission.id === this.selectedFeaturedMissionId());
    const nextMission = missions[currentIndex + direction];
    if (nextMission) {
      this.selectFeaturedMission(nextMission.id);
    }
  }

  public selectStatus(status: MissionStatus): void {
    this.selectedStatus.set(status);

    if (status === 'completed' && !this.completedCampaignsLoaded() && !this.completedCampaignsIsLoading()) {
      this.loadCompletedCampaigns();
    }
  }

  public loadActiveCampaigns(): void {
    this.activeCampaignsIsLoading.set(true);
    this.activeCampaignsLoadError.set(false);

    this.#promotionsService
      .getActiveCampaigns({ page: 1, pageSize: 10 })
      .pipe(
        tap(response => {
          this.activeCampaigns.set(response.results);
          this.activeCampaignsCount.set(response.count);
          this.selectedFeaturedMissionId.set(response.results[0]?.id ?? null);
        }),
        catchError(() => {
          this.activeCampaigns.set([]);
          this.activeCampaignsCount.set(0);
          this.selectedFeaturedMissionId.set(null);
          this.activeCampaignsLoadError.set(true);
          return of(null);
        }),
        finalize(() => {
          this.activeCampaignsIsLoading.set(false);
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }

  public loadCompletedCampaigns(page = 1): void {
    this.completedCampaignsIsLoading.set(true);
    this.completedCampaignsLoadError.set(false);

    this.#promotionsService
      .getParticipatingCampaigns({ page, pageSize: this.completedPageSize }, true)
      .pipe(
        tap(response => {
          this.completedCampaigns.set(response.results);
          this.completedCampaignsPageMeta.set({
            count: response.count,
            totalPages: Math.max(1, response.total_pages)
          });
          this.completedPage.set(page);
          this.completedCampaignsLoaded.set(true);
        }),
        catchError(() => {
          this.completedCampaigns.set([]);
          this.completedCampaignsPageMeta.set({ count: 0, totalPages: 1 });
          this.completedCampaignsLoadError.set(true);
          return of(null);
        }),
        finalize(() => {
          this.completedCampaignsIsLoading.set(false);
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }

  public loadCampaignSummary(): void {
    this.#promotionsService
      .getPromotionsSummary()
      .pipe(
        tap(summary => {
          this.campaignSummary.set(summary);
        }),
        catchError(() => {
          this.campaignSummary.set(null);
          return of(null);
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }

  public closeBanner(): void {
    this.isBannerVisible.set(false);
  }

  public goToCompletedPage(page: number): void {
    const nextPage = Math.max(1, Math.min(this.completedPages(), page));

    if (nextPage !== this.completedPage()) {
      this.loadCompletedCampaigns(nextPage);
    }
  }

  public openCampaignDetails(campaign: ICampaign): void {
    this.detailsLoadingCampaignId.set(campaign.id);
    this.detailsLoadError.set(false);

    this.#promotionsService
      .getCampaignDetail(campaign.id)
      .pipe(
        tap(detail => {
          this.#openMissionDetails(this.#mapCampaignDetail(detail));
        }),
        catchError(() => {
          this.detailsLoadError.set(true);
          return of(null);
        }),
        finalize(() => {
          this.detailsLoadingCampaignId.set(null);
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }

  #openMissionDetails(mission: IPromotionMissionDetailsModalData): void {
    this.#modalService.create({
      nzContent: PromotionMissionDetailsModalComponent,
      nzData: mission,
      nzCentered: true,
      nzClosable: false,
      nzFooter: null,
      nzWidth: 'min(calc(100vw - 2rem), 31.25rem)',
      nzBodyStyle: { padding: '0', background: 'transparent' },
      nzWrapClassName: 'promotion-modal-wrap',
      nzClassName: 'promotion-mission-details-modal-dialog'
    });
  }

  #mapCampaignDetail(detail: ICampaignDetail): IPromotionMissionDetailsModalData {
    return {
      title: detail.name,
      description: detail.description,
      statusLabel: `promotions.campaignDetails.statuses.${detail.status}`,
      objectiveProgress: detail.objective_progress,
      entriesCount: detail.user_progress.entries_count,
      maxEntriesPerUser: detail.user_progress.max_entries_per_user,
      remainingEntries: detail.user_progress.remaining_entries,
      endDate: detail.end_date,
      progress: this.#getModalProgress(detail)
    };
  }

  #getModalProgress(detail: ICampaignDetail): number | null {
    if (detail.objective_progress) {
      return this.#getProgress(detail.objective_progress.current, detail.objective_progress.target);
    }

    return detail.user_progress.max_entries_per_user
      ? this.#getProgress(detail.user_progress.entries_count, detail.user_progress.max_entries_per_user)
      : null;
  }

  #getProgress(current: number, target: number | null): number {
    if (!target) {
      return 0;
    }

    return Math.min(100, Math.max(0, (current / target) * 100));
  }

  #getCampaignIcon(campaignType: CampaignType): string {
    const icons: Record<CampaignType, string> = {
      sweepstakes: 'assets/img/promotions/missions/mission-planting.svg',
      fx4y: 'assets/img/promotions/missions/mission-community.svg',
      instant_win: 'assets/img/promotions/missions/mission-sales.svg'
    };

    return icons[campaignType];
  }

  #getCampaignIconVariant(campaignType: CampaignType): ICampaignMissionView['iconVariant'] {
    const variants: Record<CampaignType, ICampaignMissionView['iconVariant']> = {
      sweepstakes: 'tree',
      fx4y: 'sprout',
      instant_win: 'card'
    };

    return variants[campaignType];
  }
}
